import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { getImageUrl, downloadFileFromUrl, sanitizeDownloadFilename } from '../../utils/api';
import { validateFileSize, MAX_CATALOGUE_PDF_MB, formatSizeLimit } from '../../utils/imageProcessor';
import './styles/catalogues.css';

const emptyForm = () => ({ year: '', name: '', pdf_file: null });

function buildEntry(catalogue, overrides = {}) {
  return {
    id: catalogue.id ?? null,
    clientId: catalogue.clientId ?? (catalogue.id ? `existing-${catalogue.id}` : crypto.randomUUID()),
    year: catalogue.year,
    name: catalogue.name,
    pdf_path: catalogue.pdf_path ?? null,
    pdf_file: catalogue.pdf_file ?? null,
    previewUrl: catalogue.previewUrl ?? null,
    isNew: catalogue.isNew ?? false,
    isDeleted: catalogue.isDeleted ?? false,
    isModified: catalogue.isModified ?? false,
    ...overrides
  };
}

function getDisplayPdfUrl(item) {
  if (item.previewUrl) return item.previewUrl;
  if (item.pdf_path) return getImageUrl(item.pdf_path);
  return null;
}

const BrandCataloguesManager = forwardRef(({ salonId, brandId, userRole }, ref) => {
  const { t } = useLanguage();
  const canManage = userRole === 'owner' || userRole === 'admin';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formError, setFormError] = useState('');
  const [editingClientId, setEditingClientId] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [previewItem, setPreviewItem] = useState(null);
  const [downloadingPreview, setDownloadingPreview] = useState(false);

  const revokePreview = (item) => {
    if (item?.previewUrl) {
      URL.revokeObjectURL(item.previewUrl);
    }
  };

  const fetchCatalogues = useCallback(async () => {
    if (!salonId || !brandId) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `/api/salons/${salonId}/dress-brands/${brandId}/catalogues`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const catalogues = response.data.catalogues || [];
      setItems(catalogues.map((c) => buildEntry({ ...c, isNew: false })));
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || t('brands.failedToLoadCatalogues'));
    } finally {
      setLoading(false);
    }
  }, [salonId, brandId, t]);

  useEffect(() => {
    fetchCatalogues();
  }, [fetchCatalogues]);

  useEffect(() => {
    return () => {
      items.forEach((item) => {
        if (item.isNew) revokePreview(item);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setEditingClientId(null);
    setFormData(emptyForm());
    setFormError('');
  };

  const validateFormFields = () => {
    const year = parseInt(formData.year, 10);
    const name = formData.name.trim();
    if (!year || year < 1990 || year > 2100) {
      return t('brands.invalidCatalogueYear');
    }
    if (!name) {
      return t('brands.catalogueNameRequired');
    }
    if (!editingClientId && !formData.pdf_file) {
      return t('brands.cataloguePdfRequired');
    }
    return null;
  };

  const handleAddOrUpdateInList = () => {
    const validationError = validateFormFields();
    if (validationError) {
      setFormError(validationError);
      return;
    }

    const year = parseInt(formData.year, 10);
    const name = formData.name.trim();
    let previewUrl = null;
    if (formData.pdf_file) {
      previewUrl = URL.createObjectURL(formData.pdf_file);
    }

    if (editingClientId) {
      setItems((prev) =>
        prev.map((item) => {
          if (item.clientId !== editingClientId) return item;
          if (previewUrl && item.previewUrl) {
            URL.revokeObjectURL(item.previewUrl);
          }
          return buildEntry(item, {
            year,
            name,
            pdf_file: formData.pdf_file || item.pdf_file,
            previewUrl: previewUrl || item.previewUrl,
            isModified: !item.isNew,
            isNew: item.isNew
          });
        })
      );
    } else {
      setItems((prev) => [
        ...prev,
        buildEntry({
          year,
          name,
          pdf_file: formData.pdf_file,
          previewUrl,
          isNew: true
        })
      ]);
    }

    resetForm();
  };

  const handleEdit = (item) => {
    setEditingClientId(item.clientId);
    setFormData({
      year: String(item.year),
      name: item.name,
      pdf_file: null
    });
    setFormError('');
    setPreviewItem(null);
  };

  const handleView = (item) => {
    setPreviewItem(item);
  };

  const handleRemoveFromList = (clientId) => {
    setItems((prev) => {
      const target = prev.find((i) => i.clientId === clientId);
      if (!target) return prev;
      if (target.isNew) {
        revokePreview(target);
        return prev.filter((i) => i.clientId !== clientId);
      }
      return prev.map((i) =>
        i.clientId === clientId ? { ...i, isDeleted: true } : i
      );
    });
    if (editingClientId === clientId) resetForm();
  };

  const validate = () => {
    const visible = items.filter((i) => !i.isDeleted);
    for (const item of visible) {
      if (!item.year || !item.name?.trim()) {
        return t('brands.catalogueNameRequired');
      }
      if (!item.pdf_path && !item.pdf_file) {
        return t('brands.cataloguePdfRequired');
      }
    }
    return null;
  };

  const syncCatalogues = async () => {
    const token = localStorage.getItem('token');
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'multipart/form-data'
    };

    for (const item of items.filter((i) => i.isDeleted && i.id)) {
      await axios.delete(
        `/api/salons/${salonId}/dress-brands/${brandId}/catalogues/${item.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
    }

    for (const item of items.filter((i) => !i.isDeleted && i.id && i.isModified)) {
      const submitData = new FormData();
      submitData.append('year', item.year);
      submitData.append('name', item.name);
      if (item.pdf_file) {
        submitData.append('pdf_file', item.pdf_file);
      }
      await axios.put(
        `/api/salons/${salonId}/dress-brands/${brandId}/catalogues/${item.id}`,
        submitData,
        { headers }
      );
    }

    for (const item of items.filter((i) => !i.isDeleted && i.isNew)) {
      const submitData = new FormData();
      submitData.append('year', item.year);
      submitData.append('name', item.name);
      submitData.append('pdf_file', item.pdf_file);
      await axios.post(
        `/api/salons/${salonId}/dress-brands/${brandId}/catalogues`,
        submitData,
        { headers }
      );
    }
  };

  useImperativeHandle(ref, () => ({
    validate,
    syncCatalogues
  }));

  const visibleItems = items
    .filter((i) => !i.isDeleted)
    .sort((a, b) => b.year - a.year || a.name.localeCompare(b.name));

  const previewPdf = previewItem ? getDisplayPdfUrl(previewItem) : null;

  const handleDownloadPreview = async () => {
    if (!previewItem || !previewPdf || downloadingPreview) return;
    setDownloadingPreview(true);
    try {
      const filename = sanitizeDownloadFilename(`${previewItem.year}-${previewItem.name}`);
      await downloadFileFromUrl(previewPdf, filename);
    } catch {
      window.open(previewPdf, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloadingPreview(false);
    }
  };

  return (
    <div className="form-section brand-catalogues-section">
      <h4>{t('brands.catalogues')}</h4>
      {canManage && (
        <p className="catalogues-pending-hint">{t('brands.cataloguesSaveHint')}</p>
      )}

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <p className="catalogues-loading">{t('common.loading')}</p>
      ) : visibleItems.length === 0 && !canManage ? (
        <p className="catalogues-empty">{t('brands.noCatalogues')}</p>
      ) : (
        <ul className="catalogues-list">
          {visibleItems.map((item) => (
            <li key={item.clientId} className="catalogue-list-item">
              <div className="catalogue-list-item-main">
                <span className="catalogue-year">{item.year}</span>
                <span className="catalogue-name">
                  {item.name}
                  {item.isNew && <span className="catalogue-pending-badge">{t('brands.pending')}</span>}
                  {!item.isNew && item.isModified && (
                    <span className="catalogue-pending-badge">{t('brands.modified')}</span>
                  )}
                </span>
              </div>
              <div className="catalogue-list-item-actions">
                <button
                  type="button"
                  className="action-button view"
                  onClick={() => handleView(item)}
                  title={t('brands.viewCatalogues')}
                  disabled={!getDisplayPdfUrl(item)}
                >
                  👁️
                </button>
                {canManage && (
                  <>
                    <button
                      type="button"
                      className="action-button edit"
                      onClick={() => handleEdit(item)}
                      title={t('brands.editCatalogue')}
                    >
                      ✏️
                    </button>
                    <button
                      type="button"
                      className="action-button delete"
                      onClick={() => handleRemoveFromList(item.clientId)}
                      title={t('brands.deleteCatalogue')}
                    >
                      🗑️
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
          {visibleItems.length === 0 && canManage && (
            <li className="catalogues-empty">{t('brands.noCatalogues')}</li>
          )}
        </ul>
      )}

      {previewPdf && (
        <div className="catalogue-inline-preview">
          <div className="catalogue-preview-toolbar">
            <button
              type="button"
              className="sleek-button change-log"
              onClick={() => window.open(previewPdf, '_blank', 'noopener,noreferrer')}
            >
              {t('brands.openInNewTab')}
            </button>
            <button
              type="button"
              className="sleek-button change-log"
              onClick={handleDownloadPreview}
              disabled={downloadingPreview}
            >
              {downloadingPreview ? `${t('common.loading')}...` : t('brands.downloadPdf')}
            </button>
            <button type="button" className="sleek-button change-log" onClick={() => setPreviewItem(null)}>
              {t('common.close')}
            </button>
          </div>
          <iframe src={previewPdf} className="catalogue-pdf-viewer" title="Catalogue preview" />
        </div>
      )}

      {canManage && (
        <div className="catalogue-form">
          <h5>{editingClientId ? t('brands.editCatalogue') : t('brands.addCatalogue')}</h5>
          {formError && <div className="error-message">{formError}</div>}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="catalogue_year">{t('brands.catalogueYear')}</label>
              <input
                type="number"
                id="catalogue_year"
                min="1990"
                max="2100"
                value={formData.year}
                onChange={(e) => setFormData((prev) => ({ ...prev, year: e.target.value }))}
                placeholder="2026"
              />
            </div>
            <div className="form-group">
              <label htmlFor="catalogue_name">{t('brands.catalogueName')}</label>
              <input
                type="text"
                id="catalogue_name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder={t('brands.catalogueName')}
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="catalogue_pdf">
              {t('brands.cataloguePdf')}
              {!editingClientId && ' *'}
            </label>
            <input
              type="file"
              id="catalogue_pdf"
              accept="application/pdf,.pdf"
              onChange={(e) => {
                const file = e.target.files[0] || null;
                // Too big for the server: say so now instead of failing on save.
                if (file && !validateFileSize(file, MAX_CATALOGUE_PDF_MB)) {
                  setFormError(t('messages.error.fileSizeMustBeLessThan', '', { size: formatSizeLimit(MAX_CATALOGUE_PDF_MB) }));
                  e.target.value = '';
                  setFormData((prev) => ({ ...prev, pdf_file: null }));
                  return;
                }
                setFormError('');
                setFormData((prev) => ({ ...prev, pdf_file: file }));
              }}
            />
            {editingClientId && (
              <small className="form-hint">{t('brands.cataloguePdfOptionalOnEdit')}</small>
            )}
          </div>
          <div className="catalogue-form-actions">
            {editingClientId && (
              <button type="button" className="salon-button secondary" onClick={resetForm}>
                {t('common.cancel')}
              </button>
            )}
            <button
              type="button"
              className="salon-button primary"
              onClick={handleAddOrUpdateInList}
            >
              {editingClientId ? t('brands.updateCatalogueInList') : t('brands.addCatalogueToList')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

BrandCataloguesManager.displayName = 'BrandCataloguesManager';

export default BrandCataloguesManager;
